/* import __COLOCATED_TEMPLATE__ from './metric-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { JobGroupObject } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type { PercentageMetricValue } from 'embercom/lib/reporting/chart-data-resource';
import {
  and,
  not,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';

interface Signature {
  reportState: ReportState;
  jobGroupObject: JobGroupObject;
}

export default class MetricCard extends Component<Signature> {
  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.countChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use teammateInvolvedDataResource =
    this.args.jobGroupObject.id === 'improveAnswers'
      ? staticDataResource(this, {
          serializedChart: () => this.teammateInvolvedChartDefinition,
          reportState: () => this.args.reportState,
        })
      : null;

  get countChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: this.args.jobGroupObject.chartSeries,
    };
  }

  get teammateInvolvedChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: and(this.finInvolved, this.teammateWasEverInvolved, not(this.finResolved)),
        },
      ],
    };
  }

  get chartHasData() {
    let value = this.dataResource.kpiData[0] as PercentageMetricValue;
    return (
      value.value !== undefined &&
      (this.teammateInvolvedDataResource?.kpiData[0] !== undefined ||
        this.teammateInvolvedDataResource?.kpiData[0] !== 0)
    );
  }

  get finInvolved() {
    return {
      type: 'category',
      data: {
        property: 'fin.participated',
        attribute: 'conversation.fin.participated',
        values: [true],
      },
    };
  }

  get teammateWasEverInvolved() {
    return {
      type: 'exists',
      data: {
        property: 'admin_participant_ids',
        attribute: 'conversation.admin_participant_ids',
      },
    };
  }

  get finResolved() {
    return {
      type: 'category',
      data: {
        property: 'fin.resolution_state',
        attribute: 'conversation.fin.resolution_state',
        values: ['confirmed_resolved', 'assumed_resolved'],
      },
    };
  }

  get positivePercentageLabel() {
    let value = this.dataResource.kpiData[0] as PercentageMetricValue;
    if (value.value === undefined || value.value === 0) {
      return '—';
    }
    return this.dataResource.formatValue({
      value: {
        value: Math.round(value.value || 0),
        denominatorValue: value.denominatorValue,
        numeratorValue: value.numeratorValue,
      },
      metricId: this.args.jobGroupObject.chartSeries[0].metric_id,
    });
  }

  get teammateInvolvedPercentageLabel() {
    let teammateInvolvedValue = this.teammateInvolvedNumeratorValue || 0;
    let overall = this.dataResource.kpiData[0] as PercentageMetricValue;
    let denominator = overall.denominatorValue;

    return this.dataResource.formatValue({
      value: {
        value: Math.round((teammateInvolvedValue / denominator) * 100),
        denominatorValue: denominator,
        numeratorValue: teammateInvolvedValue || 0,
      },
      metricId: this.args.jobGroupObject.chartSeries[0].metric_id,
    });
  }

  get negativePercentageLabel() {
    let value = this.dataResource.kpiData[0] as PercentageMetricValue;
    let teammateInvolvedValue = this.teammateInvolvedNumeratorValue || 0;
    let mutatedValue = {
      ...value,
      value: Math.round(
        ((value.denominatorValue - value.numeratorValue - teammateInvolvedValue) /
          value.denominatorValue) *
          100,
      ),
    };
    return this.dataResource.formatValue({
      value: mutatedValue,
      metricId: this.args.jobGroupObject.chartSeries[0].metric_id,
    });
  }

  get barGroupings() {
    let metricValue = this.dataResource.kpiData[0] as PercentageMetricValue;
    let teammateInvolvedValue = this.teammateInvolvedNumeratorValue || 0;
    let positiveGroup = {
      percentage: Math.round(metricValue.value || 0),
      backgroundColor: this.args.jobGroupObject.positiveBackgroundClass,
    };
    let teammateGroup = {
      percentage: Math.round((teammateInvolvedValue / metricValue.denominatorValue) * 100),
      backgroundColor: this.args.jobGroupObject.negativeBackgroundClass,
    };
    let negativeGroup = {
      percentage: Math.round(
        ((metricValue.denominatorValue - metricValue.numeratorValue - teammateInvolvedValue) /
          metricValue.denominatorValue) *
          100,
      ),
      backgroundColor:
        this.args.jobGroupObject.id === 'improveAnswers'
          ? 'bg-neutral-border-emphasis'
          : this.args.jobGroupObject.negativeBackgroundClass,
    };

    return this.args.jobGroupObject.id === 'improveAnswers'
      ? [positiveGroup, teammateGroup, negativeGroup]
      : [positiveGroup, negativeGroup];
  }

  get gridTemplateColumns() {
    let spans = this.barGroupings.map((group) =>
      group.backgroundColor === this.args.jobGroupObject.negativeBackgroundClass
        ? group.percentage
        : group.percentage - 1,
    );
    return spans.map((span) => `${span}fr`).join(' ');
  }

  get positiveNumeratorValue() {
    return (this.dataResource.kpiData[0] as PercentageMetricValue).numeratorValue;
  }

  get negativeNumeratorValue() {
    let teammateInvolvedValue = this.teammateInvolvedNumeratorValue || 0;
    let value = this.dataResource.kpiData[0] as PercentageMetricValue;
    return value.denominatorValue - value.numeratorValue - teammateInvolvedValue;
  }

  get teammateInvolvedNumeratorValue() {
    if (this.args.jobGroupObject.id === 'improveAnswers') {
      return this.teammateInvolvedDataResource?.kpiData[0] as number | null;
    }
    return 0;
  }

  get isLoading() {
    return this.dataResource.isLoading || this.teammateInvolvedDataResource?.isLoading;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::MetricCard': typeof MetricCard;
    'reporting/automation/ai-insights/optimize/metric-card': typeof MetricCard;
  }
}
